<template lang="pug">
b-modal#approver-settings-modal(
  title="Approver Settings"
  size="lg"
  centered
  no-close-on-backdrop
  hide-footer
  @shown="loadSettings"
)
  .text-center.py-4(v-if="loading")
    b-spinner(variant="primary")
    p.mt-2.text-muted Loading approver settings...

  div(v-else)
    p.text-muted.small.
      Approvals route down each list in order: if an approver is marked unavailable
      (e.g. on leave), their duty automatically passes to the next available person
      in the same list. Amounts above the limit always need a senior approver.

    //- Tier-1 limit
    .form-section
      label.section-label Approval limit
      b-input-group(prepend="$" style="max-width: 200px")
        b-form-input(type="number" min="0" v-model.number="config.tier1Limit")
      small.text-muted Estimated credit up to this amount can be approved by the first list; above it, only senior approvers.

    //- Tier lists
    .form-section(v-for="tier in ['tier1', 'senior']" :key="tier")
      label.section-label
        | {{ tier === 'tier1' ? `Approvers — up to $${config.tier1Limit}` : `Senior approvers — above $${config.tier1Limit}` }}
      .acting-line(v-if="acting(tier)")
        Icon(icon="tabler:user-check")
        |  Acting today:
        strong {{ acting(tier).name || acting(tier).email }}
      .acting-line.text-danger(v-else-if="config[tier].length")
        Icon(icon="tabler:alert-triangle")
        |  No one in this list is available today!

      .approver-row(v-for="(a, idx) in config[tier]" :key="tier + idx")
        .row-order
          button.order-btn(:disabled="idx === 0" @click="move(tier, idx, -1)" title="Move up") ▲
          span.order-num {{ idx + 1 }}
          button.order-btn(:disabled="idx === config[tier].length - 1" @click="move(tier, idx, 1)" title="Move down") ▼
        .row-fields
          .d-flex.mb-1(style="gap: 0.5rem")
            b-form-input(size="sm" v-model="a.name" placeholder="Name (e.g. Eunice)" style="max-width: 180px")
            b-form-input(size="sm" type="email" v-model="a.email" placeholder="email@dementia.org.sg" :state="a.email ? isValidEmail(a.email) : null")
          .d-flex.align-items-center(style="gap: 0.5rem")
            small.text-muted(style="min-width: 90px") Unavailable:
            b-form-input(size="sm" type="date" v-model="a.unavailableFrom" style="max-width: 160px")
            small.text-muted to
            b-form-input(size="sm" type="date" v-model="a.unavailableTo" style="max-width: 160px")
            b-button(size="sm" variant="link" v-if="a.unavailableFrom || a.unavailableTo" @click="a.unavailableFrom = ''; a.unavailableTo = ''") clear
            span.badge-unavail(v-if="isUnavailableToday(a)") Away today
        b-button.row-remove(size="sm" variant="outline-danger" @click="removeApprover(tier, idx)" title="Remove")
          Icon(icon="tabler:trash")

      b-button(size="sm" variant="outline-primary" @click="addApprover(tier)")
        Icon(icon="tabler:plus")
        |  Add approver

    .d-flex.justify-content-end.mt-3(style="gap: 0.5rem")
      b-button(variant="outline-secondary" @click="$bvModal.hide('approver-settings-modal')" :disabled="saving") Cancel
      b-button(variant="primary" @click="save" :disabled="saving")
        b-spinner(small v-if="saving")
        span(v-else) Save Settings
</template>

<script>
import { Icon } from '@iconify/vue2';
import { mapActions } from 'vuex';
import { MAKEUP_REQUESTS_NAMESPACE, ACTIONS } from '@/store/Modules/MakeupRequests/types-makeup-requests';
import dayjs from 'dayjs';

export default {
  name: 'ApproverSettingsModal',
  components: { Icon },
  data() {
    return {
      loading: false,
      saving: false,
      config: { tier1Limit: 200, tier1: [], senior: [] }
    };
  },
  methods: {
    ...mapActions(MAKEUP_REQUESTS_NAMESPACE, {
      getApproversAction: ACTIONS.GET_APPROVERS,
      saveApproversAction: ACTIONS.SAVE_APPROVERS
    }),

    async loadSettings() {
      this.loading = true;
      try {
        const data = await this.getApproversAction();
        this.config = {
          tier1Limit: data.config.tier1Limit,
          tier1: (data.config.tier1 || []).map(a => ({ name: '', unavailableFrom: '', unavailableTo: '', ...a })),
          senior: (data.config.senior || []).map(a => ({ name: '', unavailableFrom: '', unavailableTo: '', ...a }))
        };
      } catch (e) {
        this.$bvToast.toast('Failed to load approver settings', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.loading = false;
      }
    },

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email || '').trim());
    },

    isUnavailableToday(a) {
      if (!a.unavailableFrom || !a.unavailableTo) return false;
      const today = dayjs().format('YYYY-MM-DD');
      return today >= a.unavailableFrom && today <= a.unavailableTo;
    },

    // First AVAILABLE approver in list order — mirrors the backend routing.
    acting(tier) {
      return (this.config[tier] || []).find(a => a.email && !this.isUnavailableToday(a)) || null;
    },

    addApprover(tier) {
      this.config[tier].push({ name: '', email: '', unavailableFrom: '', unavailableTo: '' });
    },

    removeApprover(tier, idx) {
      this.config[tier].splice(idx, 1);
    },

    move(tier, idx, delta) {
      const list = this.config[tier];
      const target = idx + delta;
      if (target < 0 || target >= list.length) return;
      const [item] = list.splice(idx, 1);
      list.splice(target, 0, item);
    },

    async save() {
      this.saving = true;
      try {
        await this.saveApproversAction(this.config);
        this.$bvToast.toast('Approver settings saved', { title: 'Saved', variant: 'success', solid: true });
        this.$bvModal.hide('approver-settings-modal');
      } catch (error) {
        this.$bvToast.toast(error?.response?.data?.error || 'Failed to save approver settings', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          noAutoHide: true
        });
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.form-section {
  margin-bottom: 1.25rem;
}

.section-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 0.4rem;
}

.acting-line {
  font-size: 0.82rem;
  color: #1b5e20;
  margin-bottom: 0.5rem;
}

.approver-row {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.6rem;
  border: 1px solid #ececf1;
  border-radius: 10px;
  margin-bottom: 0.5rem;
  background: #fafafc;
}

.row-order {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
}

.order-btn {
  border: none;
  background: transparent;
  color: #9ca3af;
  font-size: 0.6rem;
  cursor: pointer;
  padding: 0 0.25rem;
}

.order-btn:disabled {
  opacity: 0.3;
  cursor: default;
}

.order-num {
  font-size: 0.8rem;
  font-weight: 700;
  color: #50276b;
}

.row-fields {
  flex: 1;
}

.row-remove {
  align-self: center;
}

.badge-unavail {
  font-size: 0.72rem;
  font-weight: 700;
  background: #fff3cd;
  color: #856404;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
}
</style>
